import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { MagnifyingGlassIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { Modal } from '../../components/ui/Modal';
import { Pagination } from '../../components/ui/Pagination';
import {
  badgeClass,
  dangerButtonClass,
  Field,
  FormActions,
  formatMoney,
  formatNumber,
  getApiErrorMessage,
  inputClass,
  inputErrorClass,
  isQuantity,
  primaryButtonClass,
  QUANTITY_STEP,
  secondaryButtonClass,
} from '../../components/ui/FormControls';
import { usePermissions } from '../../hooks/usePermissions';
import { cn } from '../../lib/utils';
import { defaultRowsPerPage } from '../../lib/preferences';

type Status = 'open' | 'closed' | 'cancelled';

interface ScheduleRow {
  id: string;
  scheduleNumber: string;
  scheduleDate: string;
  customerId: string;
  customerName: string;
  customerReference: string | null;
  status: Status;
  lines: number;
  pendingLines: number;
  dueLines: number;
  nextDueDate: string | null;
}
interface SchedulePage {
  data: ScheduleRow[];
  total: number;
  totalPages: number;
}
interface ScheduleLine {
  id: string;
  productId: string;
  productName: string;
  sku: string;
  unit: string;
  dueDate: string;
  quantity: number;
  onHand: number;
  salesOrderId: string | null;
  orderNumber: string | null;
  orderStatus: string | null;
  due: boolean;
  contractRate: number | null;
}
interface Schedule extends Omit<ScheduleRow, 'lines' | 'pendingLines' | 'dueLines' | 'nextDueDate'> {
  notes: string | null;
  closedReason: string | null;
  lines: ScheduleLine[];
  orderNumber?: string;
}
interface Summary {
  open: number;
  dueLines: number;
  dueThisWeek: number;
  pendingLines: number;
}
interface Choice {
  id: string;
  name: string;
}
interface ProductChoice {
  id: string;
  name: string;
  sku: string;
  unit: string;
}
interface FormLine {
  productId: string;
  name: string;
  sku: string;
  unit: string;
  dueDate: string;
  quantity: string;
}

const PAGE_SIZES = [10, 25, 50, 100];
const thClass = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';
const STATUS: Record<Status, { label: string; className: string }> = {
  open: { label: 'Open', className: 'bg-blue-50 text-blue-700 ring-blue-600/20' },
  closed: { label: 'Closed', className: 'bg-gray-50 text-gray-600 ring-gray-500/20' },
  cancelled: { label: 'Cancelled', className: 'bg-gray-50 text-gray-500 ring-gray-500/20' },
};

const formatDate = (value?: string | null) =>
  value ? new Date(`${String(value).slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const clean = (params: object) => Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== '' && value !== false));

const scheduleApi = {
  page: async (params: object): Promise<SchedulePage> => (await api.get('/customer-schedules', { params: clean(params) })).data,
  summary: async (): Promise<Summary> => (await api.get('/customer-schedules/summary')).data,
  one: async (id: string): Promise<Schedule> => (await api.get(`/customer-schedules/${id}`)).data,
  create: async (data: object): Promise<Schedule> => (await api.post('/customer-schedules', data)).data,
  update: async (id: string, data: object): Promise<Schedule> => (await api.put(`/customer-schedules/${id}`, data)).data,
  act: async (id: string, action: string, data: object = {}): Promise<Schedule> => (await api.post(`/customer-schedules/${id}/${action}`, data)).data,
  customers: async (): Promise<Choice[]> => {
    const result = (await api.get('/customers', { params: { limit: 100 } })).data;
    return (Array.isArray(result) ? result : result?.data ?? []) as Choice[];
  },
  products: async (search: string): Promise<ProductChoice[]> => (await api.get('/inventory/paginated', { params: { search, limit: 8, page: 1 } })).data.data,
};

// ---------------------------------------------------------------- enter or revise a schedule

const ScheduleForm: React.FC<{ schedule: Schedule | null; isPending: boolean; onCancel: () => void; onSubmit: (data: object) => void }> = ({ schedule, isPending, onCancel, onSubmit }) => {
  const { data: customers = [] } = useQuery({ queryKey: ['customerSchedules', 'customers'], queryFn: scheduleApi.customers, enabled: !schedule });
  const [form, setForm] = useState({ customerId: schedule?.customerId ?? '', customerReference: schedule?.customerReference ?? '', notes: schedule?.notes ?? '' });
  // Only what is not yet on an order can still change
  const [lines, setLines] = useState<FormLine[]>(() =>
    (schedule?.lines ?? []).filter((line) => !line.salesOrderId).map((line) => ({ productId: line.productId, name: line.productName, sku: line.sku, unit: line.unit, dueDate: line.dueDate, quantity: String(line.quantity) })),
  );
  const released = (schedule?.lines ?? []).filter((line) => line.salesOrderId);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);
  const { data: choices = [], isFetching } = useQuery({ queryKey: ['customerSchedules', 'products', search], queryFn: () => scheduleApi.products(search), enabled: search.length >= 2 });

  const setLine = (index: number, patch: Partial<FormLine>) => {
    setLines((prev) => prev.map((line, position) => (position === index ? { ...line, ...patch } : line)));
    setErrors((prev) => {
      const { [`line-${index}`]: _cleared, lines: _all, ...rest } = prev;
      return rest;
    });
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (!form.customerId) next.customerId = 'Choose the customer';
    if (lines.length === 0 && released.length === 0) next.lines = 'Add at least one line';
    const seen = new Set<string>();
    lines.forEach((line, index) => {
      const quantity = Number(line.quantity);
      const key = `${line.productId}|${line.dueDate}`;
      if (!line.dueDate) next[`line-${index}`] = 'Enter the date it is wanted';
      else if (line.quantity.trim() === '' || !isQuantity(quantity) || quantity <= 0) next[`line-${index}`] = 'Enter a quantity above zero, up to 4 decimals';
      else if (seen.has(key)) next[`line-${index}`] = 'The same product is already scheduled for that day';
      seen.add(key);
    });
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    onSubmit({
      customerId: form.customerId,
      customerReference: form.customerReference.trim() || null,
      notes: form.notes.trim() || null,
      lines: lines.map((line) => ({ productId: line.productId, dueDate: line.dueDate, quantity: Number(line.quantity) })),
    });
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {schedule ? (
          <Field label="Customer" htmlFor="cs-customer-name">
            <input id="cs-customer-name" type="text" disabled value={schedule.customerName} className={inputClass} />
          </Field>
        ) : (
          <Field label="Customer" htmlFor="sc-customer" required error={errors.customerId}>
            <select id="sc-customer" value={form.customerId} onChange={(event) => { setForm((prev) => ({ ...prev, customerId: event.target.value })); setErrors((prev) => { const { customerId: _cleared, ...rest } = prev; return rest; }); }} className={cn(inputClass, errors.customerId && inputErrorClass)}>
              <option value="">Choose…</option>
              {customers.map((customer) => (<option key={customer.id} value={customer.id}>{customer.name}</option>))}
            </select>
          </Field>
        )}
        <Field label="Their schedule / PO number" htmlFor="sc-reference">
          <input id="sc-reference" type="text" maxLength={80} value={form.customerReference} onChange={(event) => setForm((prev) => ({ ...prev, customerReference: event.target.value }))} className={inputClass} />
        </Field>
        <Field label="Notes" htmlFor="sc-notes" className="sm:col-span-2">
          <input id="sc-notes" type="text" maxLength={500} value={form.notes} onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))} className={inputClass} />
        </Field>
      </div>

      {released.length > 0 && (
        <p className="rounded-md bg-gray-50 px-4 py-3 text-sm text-gray-700 ring-1 ring-gray-200">
          {released.length} line(s) are already on sales orders and stay as they are. Below is what is still only scheduled; saving replaces it with what you enter.
        </p>
      )}

      <section className="rounded-lg border border-gray-200">
        <h4 className="rounded-t-lg border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Scheduled lines · {lines.length}</h4>
        {lines.length > 0 && (
          <ul className="divide-y divide-gray-100">
            {lines.map((line, index) => (
              <li key={index} className="space-y-1 px-3 py-2">
                <div className="flex flex-wrap items-center gap-3">
                  <p className="min-w-0 flex-1 text-sm text-gray-900">
                    {line.name} <span className="text-xs text-gray-500">{line.sku}</span>
                  </p>
                  <input type="date" value={line.dueDate} onChange={(event) => setLine(index, { dueDate: event.target.value })} aria-label={`Date for ${line.name}, line ${index + 1}`} className={cn(inputClass, 'w-40 py-1')} />
                  <input type="number" min="0" step={QUANTITY_STEP} value={line.quantity} onChange={(event) => setLine(index, { quantity: event.target.value })} placeholder="Qty" aria-label={`Quantity of ${line.name}, line ${index + 1}`} className={cn(inputClass, 'w-28 py-1 text-right tabular-nums', errors[`line-${index}`] && inputErrorClass)} />
                  <span className="w-10 text-xs text-gray-500">{line.unit}</span>
                  <button type="button" onClick={() => setLines((prev) => [...prev.slice(0, index + 1), { ...line, dueDate: '', quantity: '' }, ...prev.slice(index + 1)])} className="text-xs font-semibold text-indigo-600 hover:text-indigo-500" aria-label={`Add another date for ${line.name}`}>
                    + date
                  </button>
                  <button type="button" onClick={() => setLines((prev) => prev.filter((_, position) => position !== index))} aria-label={`Remove ${line.name}, line ${index + 1}`} className="rounded-md p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600">
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
                {errors[`line-${index}`] && <p className="text-sm text-red-600">{errors[`line-${index}`]}</p>}
              </li>
            ))}
          </ul>
        )}
        <div className="border-t border-gray-200 p-3">
          <div className="relative">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input id="sc-search" type="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search product or SKU to add" aria-label="Search products to add" className={cn(inputClass, 'pl-9', errors.lines && inputErrorClass)} />
          </div>
          {errors.lines && <p className="mt-1 text-sm text-red-600">{errors.lines}</p>}
          {search.length >= 2 && (
            <ul className="mt-1 max-h-44 divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200">
              {choices.length === 0 ? (
                <li className="px-3 py-2 text-sm text-gray-500">{isFetching ? 'Searching…' : 'No product matches'}</li>
              ) : (
                choices.map((product) => (
                  <li key={product.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setLines((prev) => [...prev, { productId: product.id, name: product.name, sku: product.sku, unit: product.unit, dueDate: '', quantity: '' }]);
                        setSearchInput('');
                        setSearch('');
                        setErrors((prev) => {
                          const { lines: _cleared, ...rest } = prev;
                          return rest;
                        });
                      }}
                      className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-indigo-50"
                    >
                      <span>
                        <span className="font-medium text-gray-900">{product.name}</span> <span className="text-gray-500">{product.sku}</span>
                      </span>
                      <span className="text-xs text-gray-500">{product.unit}</span>
                    </button>
                  </li>
                ))
              )}
            </ul>
          )}
        </div>
      </section>

      <FormActions>
        <button type="button" onClick={onCancel} className={secondaryButtonClass}>
          Cancel
        </button>
        <button type="submit" disabled={isPending} className={primaryButtonClass}>
          {isPending ? 'Saving…' : schedule ? 'Save Revision' : 'Enter Schedule'}
        </button>
      </FormActions>
    </form>
  );
};

// ---------------------------------------------------------------- one schedule

const SchedulePanel: React.FC<{ scheduleId: string; canEdit: boolean; canRelease: boolean; onEdit: (schedule: Schedule) => void; onClose: () => void; onChanged: () => void }> = ({ scheduleId, canEdit, canRelease, onEdit, onClose, onChanged }) => {
  const queryClient = useQueryClient();
  const { data: schedule, isLoading, isError, error } = useQuery({ queryKey: ['customerSchedules', 'one', scheduleId], queryFn: () => scheduleApi.one(scheduleId) });
  const [chosen, setChosen] = useState<string[]>([]);
  const [asking, setAsking] = useState<'close' | 'cancel' | null>(null);
  const [reason, setReason] = useState('');

  const act = useMutation({
    mutationFn: ({ action, data }: { action: string; data?: object }) => scheduleApi.act(scheduleId, action, data),
    onSuccess: (saved, { action }) => {
      const message: Record<string, string> = { release: `Sales order ${saved.orderNumber ?? ''} raised from the schedule`, close: `${saved.scheduleNumber} closed`, cancel: `${saved.scheduleNumber} cancelled` };
      toast.success(message[action]);
      queryClient.setQueryData(['customerSchedules', 'one', scheduleId], saved);
      if (action === 'release') queryClient.invalidateQueries({ queryKey: ['salesOrders'] });
      setChosen([]);
      setAsking(null);
      setReason('');
      onChanged();
    },
    onError: (actionError) => toast.error(getApiErrorMessage(actionError, 'That could not be done'), { duration: 7000 }),
  });

  if (isLoading) return <p className="py-10 text-center text-sm text-gray-500">Loading…</p>;
  if (isError || !schedule) return <p className="py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The schedule could not be loaded.')}</p>;
  const open = schedule.status === 'open';
  const pending = schedule.lines.filter((line) => !line.salesOrderId);
  const mayPick = open && canRelease;
  const toggle = (id: string) => setChosen((prev) => (prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]));
  const dueIds = pending.filter((line) => line.due).map((line) => line.id);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-gray-700">
        <span className={cn(badgeClass, STATUS[schedule.status].className)}>{STATUS[schedule.status].label}</span>
        <span className="font-semibold text-gray-900">{schedule.customerName}</span>
        <span>Entered {formatDate(schedule.scheduleDate)}</span>
        {schedule.customerReference && <span>Their reference {schedule.customerReference}</span>}
        {schedule.notes && <span className="text-gray-500">{schedule.notes}</span>}
        {schedule.closedReason && <span className="text-gray-500">{schedule.status === 'cancelled' ? 'Cancelled' : 'Closed'}: {schedule.closedReason}</span>}
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {mayPick && <th scope="col" className={thClass}><span className="sr-only">Choose</span></th>}
              <th scope="col" className={thClass}>Wanted on</th>
              <th scope="col" className={thClass}>Product</th>
              <th scope="col" className={cn(thClass, 'text-right')}>Quantity</th>
              <th scope="col" className={cn(thClass, 'text-right')}>In stock</th>
              <th scope="col" className={cn(thClass, 'text-right')}>Contract rate</th>
              <th scope="col" className={thClass}>Sales order</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {schedule.lines.map((line) => (
              <tr key={line.id} className={cn(chosen.includes(line.id) && 'bg-indigo-50')}>
                {mayPick && (
                  <td className="px-4 py-2">
                    {!line.salesOrderId && <input type="checkbox" checked={chosen.includes(line.id)} onChange={() => toggle(line.id)} aria-label={`Release ${line.productName} wanted on ${formatDate(line.dueDate)}`} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />}
                  </td>
                )}
                <td className={cn('whitespace-nowrap px-4 py-2 text-sm', line.due && open ? 'font-medium text-red-600' : 'text-gray-900')}>
                  {formatDate(line.dueDate)}
                  {line.due && open && <span className="block text-xs">Due</span>}
                </td>
                <td className="px-4 py-2 text-sm text-gray-900">
                  {line.productName} <span className="text-xs text-gray-500">{line.sku}</span>
                </td>
                <td className="whitespace-nowrap px-4 py-2 text-right text-sm text-gray-900 tabular-nums">
                  {formatNumber(line.quantity)} <span className="text-gray-500">{line.unit}</span>
                </td>
                <td className={cn('whitespace-nowrap px-4 py-2 text-right text-sm tabular-nums', !line.salesOrderId && line.onHand < line.quantity ? 'font-medium text-amber-700' : 'text-gray-700')}>{formatNumber(line.onHand)}</td>
                <td className="whitespace-nowrap px-4 py-2 text-right text-sm text-gray-700 tabular-nums">{line.contractRate === null ? '—' : formatMoney(line.contractRate)}</td>
                <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-700">
                  {line.orderNumber ? (
                    <>
                      <Link to="/sales" className="font-medium text-indigo-600 hover:text-indigo-500">{line.orderNumber}</Link>
                      <span className="text-xs text-gray-500"> · {String(line.orderStatus).replace('_', ' ')}</span>
                    </>
                  ) : (
                    <span className="text-gray-400">Not released</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {mayPick && pending.length > 0 && (
        <p className="text-xs text-gray-500">
          Tick the lines to put on one sales order. The same product on several lines is added up; prices come from a rate contract in force, otherwise from the price list. The order then goes through the usual steps.
          {dueIds.length > 0 && (
            <button type="button" onClick={() => setChosen(dueIds)} className="ml-2 font-semibold text-indigo-600 hover:text-indigo-500">
              Tick what is due
            </button>
          )}
        </p>
      )}

      {asking ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <Field label={asking === 'close' ? 'Why is it closed?' : 'Why is it cancelled?'} htmlFor="sp-reason" required hint={asking === 'close' && pending.length > 0 ? `${pending.length} line(s) not yet released will be dropped` : undefined}>
            <input id="sp-reason" type="text" maxLength={300} value={reason} onChange={(event) => setReason(event.target.value)} className={inputClass} />
          </Field>
          <div className="mt-3 flex justify-end gap-2">
            <button type="button" onClick={() => { setAsking(null); setReason(''); }} className={secondaryButtonClass}>
              Back
            </button>
            <button type="button" disabled={act.isPending || reason.trim().length < 3} onClick={() => act.mutate({ action: asking, data: { reason: reason.trim() } })} className={dangerButtonClass}>
              {asking === 'close' ? 'Confirm Closing' : 'Confirm Cancelling'}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap justify-end gap-2 border-t border-gray-200 pt-3">
          <button type="button" onClick={onClose} className={secondaryButtonClass}>
            Close Window
          </button>
          {open && canEdit && (
            <>
              {schedule.lines.every((line) => !line.salesOrderId) && (
                <button type="button" onClick={() => setAsking('cancel')} disabled={act.isPending} className={secondaryButtonClass}>
                  Cancel Schedule
                </button>
              )}
              <button type="button" onClick={() => setAsking('close')} disabled={act.isPending} className={secondaryButtonClass}>
                Close Schedule
              </button>
              <button type="button" onClick={() => onEdit(schedule)} className={secondaryButtonClass}>
                Revise
              </button>
            </>
          )}
          {mayPick && (
            <button type="button" onClick={() => act.mutate({ action: 'release', data: { lineIds: chosen } })} disabled={act.isPending || chosen.length === 0} className={primaryButtonClass}>
              {act.isPending ? 'Releasing…' : `Release ${chosen.length || ''} to a Sales Order`.replace('  ', ' ')}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------- the page

const CustomerSchedules: React.FC = () => {
  const { can } = usePermissions();
  const canCreate = can('sales.create');
  const canEdit = can('sales.update');
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultRowsPerPage());
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<Status | ''>('open');
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Schedule | null>(null);
  const [viewingId, setViewingId] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['customerSchedules', 'page', { page, limit, search, status: statusFilter, overdueOnly }],
    queryFn: () => scheduleApi.page({ page, limit, search, status: statusFilter, overdueOnly }),
    placeholderData: keepPreviousData,
  });
  const { data: summary } = useQuery({ queryKey: ['customerSchedules', 'summary'], queryFn: scheduleApi.summary });
  const { data: viewing } = useQuery({ queryKey: ['customerSchedules', 'one', viewingId], queryFn: () => scheduleApi.one(viewingId as string), enabled: !!viewingId });

  const refreshLists = () => ['page', 'summary'].forEach((key) => queryClient.invalidateQueries({ queryKey: ['customerSchedules', key] }));
  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
  };
  const save = useMutation({
    mutationFn: (input: object) => (editing ? scheduleApi.update(editing.id, input) : scheduleApi.create(input)),
    onSuccess: (schedule) => {
      toast.success(editing ? `${schedule.scheduleNumber} revised` : `${schedule.scheduleNumber} entered`);
      queryClient.setQueryData(['customerSchedules', 'one', schedule.id], schedule);
      refreshLists();
      closeForm();
      setViewingId(schedule.id);
    },
    onError: (submitError) => toast.error(getApiErrorMessage(submitError, 'The schedule could not be saved')),
  });

  const rows = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const hasFilters = !!search || statusFilter !== 'open' || overdueOnly;
  const firstRow = total > 0 ? (page - 1) * limit + 1 : 0;
  const lastRow = Math.min(page * limit, total);
  const stats = [
    { label: 'Open schedules', value: formatNumber(summary?.open ?? 0), hint: `${formatNumber(summary?.pendingLines ?? 0)} line(s) not yet on an order` },
    { label: 'Due now', value: formatNumber(summary?.dueLines ?? 0), hint: 'Lines wanted today or earlier, not released' },
    { label: 'Due this week', value: formatNumber(summary?.dueThisWeek ?? 0), hint: 'Wanted in the next seven days' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Customer Schedules</h1>
          <p className="mt-1 text-sm text-gray-500">What customers have scheduled, by date. Lines are released into sales orders as their dates come up.</p>
        </div>
        {canCreate && (
          <button type="button" onClick={() => setFormOpen(true)} className={primaryButtonClass}>
            <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
            Enter Schedule
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{stat.label}</p>
            <p className="mt-1 text-2xl font-semibold text-gray-900">{stat.value}</p>
            <p className="mt-1 text-xs text-gray-500">{stat.hint}</p>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input type="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search by schedule number, customer, their reference or product" aria-label="Search schedules" className={cn(inputClass, 'pl-9')} />
          </div>
          <select value={statusFilter} onChange={(event) => { setStatusFilter(event.target.value as Status | ''); setPage(1); }} aria-label="Status" className={cn(inputClass, 'lg:w-40')}>
            <option value="">All</option>
            {(Object.keys(STATUS) as Status[]).map((status) => (<option key={status} value={status}>{STATUS[status].label}</option>))}
          </select>
          <label className="flex items-center gap-2 whitespace-nowrap text-sm text-gray-700">
            <input type="checkbox" checked={overdueOnly} onChange={(event) => { setOverdueOnly(event.target.checked); setPage(1); }} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
            Something due
          </label>
        </div>

        <div className={cn('overflow-x-auto', isFetching && data && 'opacity-60')}>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className={thClass}>Schedule</th>
                <th scope="col" className={thClass}>Customer</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Lines</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Not released</th>
                <th scope="col" className={thClass}>Next wanted</th>
                <th scope="col" className={thClass}>Status</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isError ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The schedules could not be loaded.')}</td></tr>
              ) : isLoading ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-500">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <p className="text-sm font-medium text-gray-900">{hasFilters ? 'No schedule matches' : 'No open schedules'}</p>
                    <p className="mt-1 text-sm text-gray-500">Enter a schedule when a customer sends their requirements by date.</p>
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-4 py-3">
                      <button type="button" onClick={() => setViewingId(row.id)} className="text-sm font-medium text-indigo-600 hover:text-indigo-500">{row.scheduleNumber}</button>
                      <p className="mt-0.5 text-xs text-gray-500">{[formatDate(row.scheduleDate), row.customerReference].filter(Boolean).join(' · ')}</p>
                    </td>
                    <td className="max-w-xs truncate px-4 py-3 text-sm text-gray-900">{row.customerName}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">{formatNumber(row.lines)}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">
                      {formatNumber(row.pendingLines)}
                      {row.dueLines > 0 && <span className="block text-xs font-medium text-red-600">{formatNumber(row.dueLines)} due</span>}
                    </td>
                    <td className={cn('whitespace-nowrap px-4 py-3 text-sm', row.dueLines > 0 ? 'font-medium text-red-600' : 'text-gray-700')}>{formatDate(row.nextDueDate)}</td>
                    <td className="whitespace-nowrap px-4 py-3"><span className={cn(badgeClass, STATUS[row.status].className)}>{STATUS[row.status].label}</span></td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <button type="button" onClick={() => setViewingId(row.id)} className="rounded-md px-2.5 py-1 text-xs font-semibold text-indigo-600 ring-1 ring-inset ring-indigo-200 hover:bg-indigo-50" aria-label={`Open ${row.scheduleNumber}`}>
                        Open
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
            <span>Showing {firstRow}–{lastRow} of {formatNumber(total)}</span>
            <label className="flex items-center gap-2">
              Rows per page
              <select value={limit} onChange={(event) => { setLimit(Number(event.target.value)); setPage(1); }} className="rounded-md border border-gray-300 bg-white py-1 pl-2 pr-7 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                {PAGE_SIZES.map((size) => (<option key={size} value={size}>{size}</option>))}
              </select>
            </label>
          </div>
          {totalPages > 1 && <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />}
        </div>
      </div>

      <Modal isOpen={formOpen || !!viewingId} onClose={() => (formOpen ? closeForm() : setViewingId(null))} title={formOpen ? (editing ? `Revise ${editing.scheduleNumber}` : 'Enter Customer Schedule') : viewing?.scheduleNumber ?? 'Customer Schedule'} size="xl">
        {formOpen ? (
          <ScheduleForm key={editing?.id ?? 'new'} schedule={editing} isPending={save.isPending} onCancel={closeForm} onSubmit={(input) => save.mutate(input)} />
        ) : (
          viewingId && (
            <SchedulePanel
              key={viewingId}
              scheduleId={viewingId}
              canEdit={canEdit}
              canRelease={canCreate}
              onEdit={(schedule) => {
                setEditing(schedule);
                setFormOpen(true);
              }}
              onClose={() => setViewingId(null)}
              onChanged={refreshLists}
            />
          )
        )}
      </Modal>
    </div>
  );
};

export default CustomerSchedules;
